import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { AppShellComponent, ShellNavItem } from '@tangible/shared';
import { filter, Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { Error404PageComponent } from './error-404-page/error-404-page.component';
import { ForbiddenPageComponent } from './forbidden-page/forbidden-page.component';
import { AccessDeniedPageComponent } from './access-denied-page/access-denied-page.component';
import { SettingsService } from './settings-page/settings-service/settings.service';
import { UserService } from './services/user.service';
import { AgentDrawerComponent } from './agent-drawer/agent-drawer.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, NgClass, AppShellComponent, AgentDrawerComponent],
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  settingsService = inject(SettingsService);
  userService = inject(UserService);

  // Whether the app shell (navigation menu and agents panel) should be
  // shown. Hidden on error and forbidden pages.
  showAppShell = signal(true);
  agentPanelOpen = signal(false);
  private subscription: Subscription = new Subscription();
  private user$ = toObservable(this.userService.user);

  ngOnInit() {
    this.subscription.add(
      this.settingsService.settings$
        .pipe(filter((settings) => settings != null))
        .subscribe((settings) => {
          this.menuItems.update((items) =>
            items.map((item) => {
              if (item.routerLink === '/equipment') {
                return {
                  ...item,
                  visible: settings.trade_study_tool_features_enabled,
                };
              }
              if (item.routerLink === '/consumables') {
                return {
                  ...item,
                  visible: settings.consumables_enabled,
                };
              }
              if (item.routerLink === '/billing') {
                return { ...item, visible: settings.token_billing_enabled };
              }
              if (item.routerLink === '/auto-molds') {
                return {
                  ...item,
                  visible: settings.auto_mould_generation_enabled,
                };
              }
              return item;
            }),
          );
        }),
    );

    this.subscription.add(
      this.user$.subscribe((user) => {
        this.menuItems.update((items) =>
          items.map((item) =>
            item.routerLink === '/team'
              ? { ...item, visible: user?.role != null }
              : item,
          ),
        );
        if (user?.role !== 'superuser') {
          this.agentPanelOpen.set(false);
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  toggleAgentPanel(): void {
    this.agentPanelOpen.update((open) => !open);
  }

  menuItems = signal<ShellNavItem[]>([
    {
      label: 'Quotes',
      icon: 'pi pi-file',
      routerLink: '/quotes',
      visible: true,
      urlPrefixes: ['/quote/'],
    },
    {
      label: 'Blueprints',
      icon: 'pi pi-th-large',
      routerLink: '/blueprints',
      visible: true,
    },
    {
      label: 'Materials',
      icon: 'pi pi-database',
      routerLink: '/materials',
      visible: true,
    },
    {
      label: 'Equipment',
      icon: 'pi pi-warehouse',
      routerLink: '/equipment',
      visible: false,
    },
    {
      label: 'Consumables',
      icon: 'pi pi-wrench',
      routerLink: '/consumables',
      visible: false,
    },
    {
      label: 'Pricing Models',
      icon: 'pi pi-tag',
      routerLink: '/pricing-models',
      visible: true,
    },
    {
      label: 'Customers',
      icon: 'pi pi-building',
      routerLink: '/customers',
      visible: true,
    },
    {
      label: 'Storage',
      icon: 'pi pi-box',
      routerLink: '/storage',
      visible: true,
    },
    {
      label: 'Auto Molds',
      icon: 'pi pi-sitemap',
      routerLink: '/auto-molds',
      visible: false,
    },
    { label: 'Team', icon: 'pi pi-users', routerLink: '/team', visible: false },
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      routerLink: '/settings',
      visible: true,
    },
    {
      label: 'Billing',
      icon: 'pi pi-receipt',
      routerLink: '/billing',
      visible: false,
    },
  ]);

  logout = async () => {
    await this.userService.logout();
  };

  // If the new route is an error or forbidden page, hide the app shell.
  newRouteActivated = (componentRef: unknown) => {
    const hideShell =
      componentRef instanceof Error404PageComponent ||
      componentRef instanceof ForbiddenPageComponent ||
      componentRef instanceof AccessDeniedPageComponent;
    this.showAppShell.set(!hideShell);
    if (hideShell) {
      this.agentPanelOpen.set(false);
    }
  };

  login() {
    this.userService.redirectToLogin();
  }
}
